import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  BookOpen,
  Lightbulb,
  AlertTriangle,
  Quote,
  Briefcase,
  User,
  // Hero icons per post
  type LucideIcon,
  CalendarClock,
  ClipboardCheck,
  SearchX,
  BrainCircuit,
  ShieldCheck,
  FileEdit,
  FileSearch,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';
import { blogPosts, BlogSection } from '../data/blogPosts';

const categoryStyles: Record<string, { bg: string; text: string; dot: string; heroBg: string }> = {
  blue:    { bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-500',    heroBg: 'from-cyan-50 to-cyan-100/50' },
  green:   { bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500',   heroBg: 'from-green-50 to-green-100/50' },
  red:     { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     heroBg: 'from-red-50 to-red-100/50' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500',  heroBg: 'from-purple-50 to-purple-100/50' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', heroBg: 'from-emerald-50 to-emerald-100/50' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  dot: 'bg-indigo-500',  heroBg: 'from-indigo-50 to-indigo-100/50' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   heroBg: 'from-amber-50 to-amber-100/50' },
};

const heroIcons: Record<string, LucideIcon> = {
  'calendar-clock':  CalendarClock,
  'clipboard-check': ClipboardCheck,
  'search-x':        SearchX,
  'brain-circuit':   BrainCircuit,
  'shield-check':    ShieldCheck,
  'file-edit':       FileEdit,
  'file-search':     FileSearch,
};

const calloutStyles = {
  insight: {
    border: 'border-primary-300',
    bg: 'bg-primary-50/60',
    icon: Lightbulb,
    iconColor: 'text-primary-600',
    label: 'Key insight',
  },
  warning: {
    border: 'border-amber-300',
    bg: 'bg-amber-50/60',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Important',
  },
  example: {
    border: 'border-green-300',
    bg: 'bg-green-50/60',
    icon: Quote,
    iconColor: 'text-green-600',
    label: 'Example',
  },
};

const SectionRenderer: React.FC<{ section: BlogSection }> = ({ section }) => {
  switch (section.type) {
    case 'heading2':
      return (
        <h2 className="text-2xl font-bold text-medical-900 mt-10 mb-4">
          {section.content}
        </h2>
      );
    case 'heading3':
      return (
        <h3 className="text-xl font-semibold text-medical-800 mt-8 mb-3">
          {section.content}
        </h3>
      );
    case 'paragraph':
      return (
        <p className="text-medical-700 leading-relaxed mb-4">
          {section.content}
        </p>
      );
    case 'callout': {
      const variant = section.variant ?? 'insight';
      const style = calloutStyles[variant];
      const Icon = style.icon;
      return (
        <div className={`rounded-xl border ${style.border} ${style.bg} p-5 mb-6`}>
          <div className="flex items-start space-x-3">
            <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${style.iconColor}`} />
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${style.iconColor}`}>
                {style.label}
              </p>
              <p className="text-medical-700 leading-relaxed text-sm">
                {section.content}
              </p>
            </div>
          </div>
        </div>
      );
    }
    case 'scenario':
      return (
        <div className="rounded-xl border border-medical-200 bg-medical-50/50 p-5 mb-6">
          <div className="flex items-start space-x-3">
            <Briefcase className="h-5 w-5 mt-0.5 flex-shrink-0 text-medical-500" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1 text-medical-500">
                In practice
              </p>
              <p className="text-medical-700 leading-relaxed text-sm italic">
                {section.content}
              </p>
            </div>
          </div>
        </div>
      );
    case 'bullets':
      return (
        <ul className="space-y-3 mb-6 pl-1">
          {section.items?.map((item, i) => {
            const [label, ...rest] = item.split(' \u2014 ');
            const hasLabel = rest.length > 0;
            return (
              <li key={i} className="flex items-start space-x-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                <span className="text-medical-700 leading-relaxed">
                  {hasLabel ? (
                    <>
                      <span className="font-semibold text-medical-900">{label}</span>
                      {' \u2014 '}
                      {rest.join(' \u2014 ')}
                    </>
                  ) : (
                    item
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      );
    case 'ordered':
      return (
        <ol className="space-y-3 mb-6 pl-1 list-none counter-reset-none">
          {section.items?.map((item, i) => {
            const [label, ...rest] = item.split(' \u2014 ');
            const hasLabel = rest.length > 0;
            return (
              <li key={i} className="flex items-start space-x-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-primary-50 border border-primary-200 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-medical-700 leading-relaxed">
                  {hasLabel ? (
                    <>
                      <span className="font-semibold text-medical-900">{label}</span>
                      {' \u2014 '}
                      {rest.join(' \u2014 ')}
                    </>
                  ) : (
                    item
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      );
    case 'divider':
      return <hr className="my-10 border-medical-200" />;
    default:
      return null;
  }
};

export const BlogPostPage: React.FC = () => {
  useForceLightMode();
  const { slug } = useParams<{ slug: string }>();

  const postIndex = blogPosts.findIndex((p) => p.slug === slug);
  const post = blogPosts[postIndex];

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const style = categoryStyles[post.categoryColor] ?? categoryStyles.blue;

  const prevPost = postIndex > 0 ? blogPosts[postIndex - 1] : null;
  const nextPost = postIndex < blogPosts.length - 1 ? blogPosts[postIndex + 1] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader fixed />

      {/* Hero banner */}
      {post.ogImage ? (
        <div className="pt-28">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
            <img
              src={post.ogImage}
              alt={post.title}
              className="w-full h-auto rounded-2xl shadow-lg border border-medical-200/50"
            />
          </div>
        </div>
      ) : (() => {
        const HeroIcon = heroIcons[post.heroIcon];
        return HeroIcon ? (
          <div className={`pt-28 bg-gradient-to-b ${style.heroBg}`}>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 flex items-center justify-center">
              <div className={`w-20 h-20 rounded-2xl bg-white shadow-lg border border-medical-200/50 flex items-center justify-center`}>
                <HeroIcon className={`h-10 w-10 ${style.text}`} />
              </div>
            </div>
          </div>
        ) : (
          <div className="pt-28" />
        );
      })()}

      {/* Article */}
      <article className="pt-8 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link
            to="/blog"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center space-x-1.5 text-sm text-medical-500 hover:text-primary-600 transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to all posts</span>
          </Link>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span
              className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
              <span>{post.category}</span>
            </span>
            <span className="inline-flex items-center space-x-1 text-xs text-medical-400">
              <Clock className="h-3 w-3" />
              <span>{post.readingTime}</span>
            </span>
            <time className="text-xs text-medical-400">
              {new Date(post.publishedDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </time>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-medical-900 tracking-tight mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Subtitle */}
          <p className="text-lg text-medical-500 leading-relaxed mb-6 border-l-4 border-primary-300 pl-4">
            {post.subtitle}
          </p>

          {/* Author */}
          <div className="flex items-center space-x-3 mb-10 pb-8 border-b border-medical-200">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <User className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-medical-900">{post.author}</p>
              <p className="text-xs text-medical-500">{post.authorRole}</p>
            </div>
          </div>

          {/* Body */}
          <div className="prose-clinlexis">
            {post.sections.map((section, i) => (
              <SectionRenderer key={i} section={section} />
            ))}
          </div>
        </div>
      </article>

      {/* Prev / Next navigation */}
      <nav className="border-t border-medical-200 bg-white/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {prevPost ? (
            <Link
              to={`/blog/${prevPost.slug}`}
              onClick={() => window.scrollTo(0, 0)}
              className="group flex flex-col p-4 rounded-xl border border-medical-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <span className="text-xs text-medical-400 mb-1 flex items-center space-x-1">
                <ArrowLeft className="h-3 w-3" />
                <span>Previous</span>
              </span>
              <span className="text-sm font-medium text-medical-800 group-hover:text-primary-700 transition-colors line-clamp-2">
                {prevPost.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
          {nextPost ? (
            <Link
              to={`/blog/${nextPost.slug}`}
              onClick={() => window.scrollTo(0, 0)}
              className="group flex flex-col items-end text-right p-4 rounded-xl border border-medical-200 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <span className="text-xs text-medical-400 mb-1 flex items-center space-x-1">
                <span>Next</span>
                <ArrowRight className="h-3 w-3" />
              </span>
              <span className="text-sm font-medium text-medical-800 group-hover:text-primary-700 transition-colors line-clamp-2">
                {nextPost.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </nav>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-10 text-center shadow-xl shadow-primary-200/50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '32px 32px',
                }}
              />
            </div>
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to try ClinLexis?
              </h2>
              <p className="text-primary-100 mb-6">
                Start preparing cases faster with AI-powered chronology, guideline compliance and report drafting.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/signup"
                  className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-lg font-semibold hidden lg:flex items-center space-x-2 group shadow-lg transition-all"
                >
                  <span>Get started</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/demo"
                  className="text-white/90 hover:text-white font-semibold px-6 py-3 border border-white/30 rounded-lg hover:border-white/60 transition-all"
                >
                  Watch the Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-medical-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2">
          <p className="text-sm text-medical-400">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales
          </p>
        </div>
      </footer>
    </div>
  );
};
